import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { TrendingUp } from "lucide-react";

export function RevenueChart({ data_revenue }) {
  // Calculate totals for summary
  const totals = data_revenue.reduce(
    (acc, curr) => ({
      revenue: acc.revenue + (curr.revenue || 0),
      cost: acc.cost + (curr.cost || 0),
    }),
    { revenue: 0, cost: 0 },
  );

  const profit = totals.revenue - totals.cost;
  const profitMargin =
    totals.revenue > 0 ? ((profit / totals.revenue) * 100).toFixed(1) : 0;

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    const revenue = payload.find((p) => p.dataKey === "revenue")?.value || 0;
    const cost = payload.find((p) => p.dataKey === "cost")?.value || 0;
    const profit = revenue - cost;
    const margin = revenue > 0 ? ((profit / revenue) * 100).toFixed(1) : 0;

    return (
      <div className="rounded-lg border border-border bg-card/95 backdrop-blur-sm p-4 shadow-lg">
        <p className="font-semibold text-foreground mb-2">{label}</p>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-primary" />
              <span className="text-sm text-muted-foreground">Revenue</span>
            </div>
            <span className="text-sm font-semibold text-foreground">
              ₹{revenue.toLocaleString("en-IN")}
            </span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-warning" />
              <span className="text-sm text-muted-foreground">Cost</span>
            </div>
            <span className="text-sm font-semibold text-foreground">
              ₹{cost.toLocaleString("en-IN")}
            </span>
          </div>
          <div className="pt-2 border-t border-border">
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-muted-foreground">Profit</span>
              <span className="text-sm font-semibold text-success">
                ₹{profit.toLocaleString("en-IN")} ({margin}%)
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Format Y-axis values
  const formatYAxis = (value) => {
    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(0)}k`;
    return `₹${value}`;
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-foreground text-lg">
              Revenue vs Cost Analysis
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Hourly breakdown of sales performance
            </p>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2 text-success">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm font-medium">
                {profitMargin}% Margin
              </span>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
            <p className="text-xs text-muted-foreground mb-1">Total Revenue</p>
            <p className="text-lg font-bold text-primary">
              ₹{totals.revenue.toLocaleString("en-IN")}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-warning/5 border border-warning/20">
            <p className="text-xs text-muted-foreground mb-1">Total Cost</p>
            <p className="text-lg font-bold text-warning">
              ₹{totals.cost.toLocaleString("en-IN")}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-success/5 border border-success/20">
            <p className="text-xs text-muted-foreground mb-1">Profit</p>
            <p className="text-lg font-bold text-success">
              ₹{profit.toLocaleString("en-IN")}
            </p>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="p-6">
        {data_revenue && data_revenue.length > 0 ? (
          <>
            <div className="h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={data_revenue}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="colorRevenue"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="hsl(var(--primary))"
                        stopOpacity={0.3}
                      />
                      <stop
                        offset="95%"
                        stopColor="hsl(var(--primary))"
                        stopOpacity={0}
                      />
                    </linearGradient>
                    <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor="hsl(var(--warning))"
                        stopOpacity={0.3}
                      />
                      <stop
                        offset="95%"
                        stopColor="hsl(var(--warning))"
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                    vertical={false}
                    opacity={0.5}
                  />
                  <XAxis
                    dataKey="time"
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fill: "hsl(var(--muted-foreground))",
                      fontSize: 12,
                    }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fill: "hsl(var(--muted-foreground))",
                      fontSize: 12,
                    }}
                    tickFormatter={formatYAxis}
                    dx={-10}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorRevenue)"
                    animationDuration={1000}
                  />
                  <Area
                    type="monotone"
                    dataKey="cost"
                    stroke="hsl(var(--warning))"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorCost)"
                    animationDuration={1000}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-6 pt-4 border-t border-border">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-primary" />
                <span className="text-sm font-medium text-muted-foreground">
                  Revenue
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-warning" />
                <span className="text-sm font-medium text-muted-foreground">
                  Food Cost
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-success" />
                <span className="text-sm font-medium text-muted-foreground">
                  Profit
                </span>
              </div>
            </div>
          </>
        ) : (
          <div className="h-[320px] flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No hourly data available</p>
              <p className="text-sm mt-1">Data will appear as orders come in</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
